import {
  PHBSchema,
  ParagraafSchema,
  SPPBSchema,
  SubparagraafSchema,
  VSPBSchema,
  VoorschriftSchema,
} from '@/api/generated/digiVAPI.schemas';
import {
  LinkParagraaf,
  useParagraaf,
  useSubparagraaf,
  useVoorschrift,
} from '@/api/queries/objects';
import { fetchInFilter } from '@/utils/fetchInFilter';

export type LinksWithData = LinkParagraaf[];

export type LinkParagraafWithData = {
  type: 'paragraaf';
  data: ParagraafSchema;
  PHB: PHBSchema;
  subparagrafen: LinkSubparagraafWithData[];
};

export type LinkSubparagraafWithData = {
  type: 'subparagraaf';
  data: SubparagraafSchema;
  SPPB: SPPBSchema;
  voorschriften: LinkVoorschriftWithData[];
};

export type LinkVoorschriftWithData = {
  type: 'voorschrift';
  data: VoorschriftSchema;
  VSPB: VSPBSchema;
};

/**
 * Fetch all data for the links in the given array of LinkParagraaf objects.
 */
export const fetchAllLinksData = async (links: LinkParagraaf[]) => {
  const paragraafIds: string[] = [];
  const subparagraafIds: string[] = [];
  const voorschriftIds: string[] = [];

  links.forEach((link) => {
    paragraafIds.push(link.PHB.Paragraaf);

    link.subparagrafen.forEach((subparagraafLink) => {
      subparagraafIds.push(subparagraafLink.SPPB.Subparagraaf);

      subparagraafLink.voorschriften.forEach(({ VSPB }) =>
        voorschriftIds.push(VSPB.Voorschrift)
      );
    });
  });

  /**
   * Fetch all objects
   */
  const [paragrafen, subparagrafen, voorschriften] = await Promise.all([
    fetchInFilter(
      ({ value, page, size }) =>
        useParagraaf.fetchQuery({
          page,
          size,
          filter: [{ field: 'ID', op: 'in', value }],
        }),
      paragraafIds
    ),
    fetchInFilter(
      ({ value, page, size }) =>
        useSubparagraaf.fetchQuery({
          page,
          size,
          filter: [{ field: 'ID', op: 'in', value }],
        }),
      subparagraafIds
    ),
    fetchInFilter(
      ({ value, page, size }) =>
        useVoorschrift.fetchQuery({
          page,
          size,
          filter: [{ field: 'ID', op: 'in', value }],
        }),
      voorschriftIds
    ),
  ]);

  const paragrafenLinksWithData: LinkParagraafWithData[] = [];

  for (const link of links) {
    const paragraaf = paragrafen.find(
      (paragraaf) => paragraaf.ID === link.PHB.Paragraaf
    );

    if (!paragraaf) continue;

    const paragraafLinkWithData: LinkParagraafWithData = {
      type: 'paragraaf',
      PHB: link.PHB,
      data: paragraaf,
      subparagrafen: [],
    };

    paragrafenLinksWithData.push(paragraafLinkWithData);

    for (const subparagraafLink of link.subparagrafen) {
      const subparagraaf = subparagrafen.find(
        (subparagraaf) => subparagraaf.ID === subparagraafLink.SPPB.Subparagraaf
      );

      if (!subparagraaf) continue;

      const subparagraafLinkWithData: LinkSubparagraafWithData = {
        type: 'subparagraaf',
        SPPB: subparagraafLink.SPPB,
        data: subparagraaf,
        voorschriften: [],
      };

      paragraafLinkWithData.subparagrafen.push(subparagraafLinkWithData);

      for (const voorschriftLink of subparagraafLink.voorschriften) {
        const voorschrift = voorschriften.find(
          (voorschrift) => voorschrift.ID === voorschriftLink.VSPB.Voorschrift
        );

        if (!voorschrift) continue;

        subparagraafLinkWithData.voorschriften.push({
          type: 'voorschrift',
          VSPB: voorschriftLink.VSPB,
          data: voorschrift,
        });
      }
    }
  }

  return paragrafenLinksWithData;
};
